import { Building2, Home } from 'lucide-react';
import type { MessagingApartmentCard } from '@/shared/api';
import { formatBookingRange, statusLabels } from '@/features/bookings/components/bookingsFormat';
import type { BookingStatus } from '@/shared/api';
import { AccessInfoReveal } from './AccessInfoReveal';
import { PanelCard } from './PanelCard';
import { ProvenanceTag } from './ProvenanceTag';

function statusText(status: string): string {
  return statusLabels[status as BookingStatus] ?? status;
}

// Eine Reservierung ueber mehrere Apartments: je Apartment eine Karte mit eigenem, ausdruecklich
// gewaehltem Zugangsdaten-Abruf. Die Menge kommt bereits zugriffsgefiltert vom Server — hier wird
// nichts hochgerechnet und keine Gesamtzahl aus einer anderen Quelle gezogen.
export function ReservationApartmentsCard({
  threadId,
  apartments,
  canViewPii,
}: {
  threadId: string;
  apartments: MessagingApartmentCard[];
  canViewPii: boolean;
}) {
  const single = apartments.length === 1;
  return (
    <PanelCard
      icon={single ? Home : Building2}
      title={single ? 'Apartment und Zugang' : `Apartments (${apartments.length})`}
      hint="Türcode und WLAN erst auf Abruf"
      trailing={<ProvenanceTag source="internal" />}
    >
      {!single && (
        <p className="text-ink-faint mb-2.5 text-[11.5px]">
          Diese Reservierung umfasst mehrere Apartments. Zugangsdaten werden je Apartment angezeigt.
        </p>
      )}
      <ul className="flex flex-col gap-2.5">
        {apartments.map((apartment) => (
          <li
            key={apartment.bookingId}
            className="border-line/70 rounded-md border px-2.5 py-2 last:mb-0"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-ink text-[13px] font-medium">{apartment.propertyName}</span>
              <span
                className={`text-[10px] font-bold uppercase tracking-[0.05em] ${
                  apartment.status === 'Cancelled' ? 'text-sealwax' : 'text-ink-faint'
                }`}
              >
                {statusText(apartment.status)}
              </span>
            </div>
            <p className="text-ink-soft mt-0.5 font-mono text-[12px]">
              {formatBookingRange(apartment.checkIn, apartment.checkOut)}
            </p>
            {/* Darf der Nutzer Gast-PII sehen, ist ein fehlender Name eine Aussage ueber die
                Daten — keine Zeile zu rendern machte ihn von "kein Recht" ununterscheidbar. */}
            {canViewPii && (
              <p className="text-ink-soft mt-0.5 text-[12px]">
                Gast:{' '}
                {apartment.guestName ?? <span className="text-ink-faint">nicht hinterlegt</span>}
              </p>
            )}
            <AccessInfoReveal threadId={threadId} bookingId={apartment.bookingId} />
          </li>
        ))}
      </ul>
    </PanelCard>
  );
}
